import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { Loader2, CheckCircle2, RefreshCw, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { HUMAN_TIMEOUT_OPTIONS, makeHumanTimeoutHandler } from './whatsappAutoReturn';

const progressColor = (percent) => {
  if (percent >= 100) return 'bg-red-500';
  if (percent >= 90)  return 'bg-orange-500';
  if (percent >= 80)  return 'bg-yellow-500';
  return 'bg-green-500';
};

const statusLabel = (status) => {
  if (status === 'SUCCESS') return { label: 'Completado', cls: 'bg-green-500/10 text-green-400' };
  if (status === 'FAILED')  return { label: 'Fallido',    cls: 'bg-red-500/10 text-red-400' };
  if (status === 'PENDING') return { label: 'Pendiente',  cls: 'bg-yellow-500/10 text-yellow-400' };
  return { label: status, cls: 'bg-zinc-700 text-zinc-300' };
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return iso;
  }
};

export const WhatsAppDashboard = () => {
  const { api } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats]             = useState(null);
  const [loading, setLoading]         = useState(true);
  const [toggling, setToggling]       = useState(false);
  const [savingTimeout, setSavingTimeout] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/whatsapp/stats');
      setStats(res.data);
    } catch {
      toast.error(t('success.whatsappDashboard.statsLoadError'));
    } finally {
      setLoading(false);
    }
  }, [api, t]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleToggleAutoAddon = async () => {
    if (!stats) return;
    const next = !stats.autoAddonEnabled;
    setToggling(true);
    try {
      await api.put('/whatsapp/config', { autoAddonEnabled: next });
      setStats(s => ({ ...s, autoAddonEnabled: next }));
      toast.success(next ? 'Ampliaciones automáticas activadas' : 'Ampliaciones automáticas desactivadas');
    } catch {
      toast.error(t('success.whatsappDashboard.configUpdateError'));
    } finally {
      setToggling(false);
    }
  };

  // R4: guarda el timeout de auto-return HUMAN→IA en el backend. La lógica vive
  // en `makeHumanTimeoutHandler` (módulo `whatsappAutoReturn`) para poder
  // unit-testearla en runtime sin montar este dashboard. Mismo patrón que
  // handleToggleAutoAddon: PUT /whatsapp/config con `humanTimeoutMinutes` +
  // update optimista del state local.
  const handleChangeHumanTimeout = makeHumanTimeoutHandler({
    api,
    hasStats: !!stats,
    setStats,
    setSavingTimeout,
    t,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!stats) return null;

  const hasPlan = stats.plan !== null && stats.plan !== '';

  const WA_COUNTER_LABELS = {
    wa_messages:      'Mensajes',
    wa_pay_reminders: 'Recordatorios de pago',
  };

  const addonLabels = {
    wa_messages:      'mensajes de WhatsApp/mes',
    wa_pay_reminders: 'recordatorios de pago/mes',
    ia_phone:         'minutos de secretaria IA/mes',
  };

  const planItems = Object.entries(stats.addons ?? {})
    .filter(([, v]) => v.included > 0)
    .map(([key, v]) => `${v.included} ${addonLabels[key] ?? key}`);

  return (
    <div className="space-y-6">

      {/* SECCIÓN 1 — Créditos del mes */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-zinc-400" />
          <p className="text-xs text-zinc-500 uppercase tracking-wider">Créditos del mes</p>
        </div>

        {Object.entries(WA_COUNTER_LABELS).map(([key, label]) => {
          const counter  = stats.addons?.[key] ?? { included: 0, remaining: 0 };
          const used     = Math.max(0, counter.included - counter.remaining);
          const percent  = counter.included > 0 ? Math.min(100, (used / counter.included) * 100) : 0;
          return (
            <div key={key} className="space-y-1">
              <p className="text-xs text-zinc-400">{label}</p>
              {counter.included === 0 ? (
                <p className="text-xs text-zinc-600">No incluido en tu plan</p>
              ) : (
                <>
                  <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all rounded-full ${progressColor(percent)}`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  <p className="text-xs text-zinc-500">{used} de {counter.included} usados este mes</p>
                </>
              )}
            </div>
          );
        })}

        {stats.nextResetDate && (
          <p className="text-xs text-zinc-500">
            Se resetean el{' '}
            {new Date(stats.nextResetDate + 'T00:00:00').toLocaleDateString('es-ES', {
              day: '2-digit', month: 'long', year: 'numeric',
            })}
          </p>
        )}
      </div>

      {/* SECCIÓN 2 — Ampliaciones automáticas */}
      <div className="flex items-start justify-between gap-4 pt-4 border-t border-zinc-800">
        <div className="space-y-1">
          <p className="text-sm font-medium text-zinc-200">Ampliaciones automáticas de WhatsApp</p>
          <p className="text-xs text-zinc-500 leading-relaxed max-w-sm">
            {stats.autoAddonEnabled
              ? 'Cuando agotes los mensajes incluidos, se añadirán automáticamente más créditos para que tus recordatorios no se interrumpan.'
              : 'Cuando agotes los créditos incluidos, los recordatorios se pausarán hasta el próximo ciclo de facturación.'}
          </p>
        </div>
        <Switch
          checked={stats.autoAddonEnabled}
          onCheckedChange={handleToggleAutoAddon}
          disabled={toggling}
        />
      </div>

      {/* SECCIÓN 2b — Auto-return a IA por inactividad humana (R4) */}
      <div className="flex items-start justify-between gap-4 pt-4 border-t border-zinc-800">
        <div className="space-y-1">
          <p className="text-sm font-medium text-zinc-200">
            Volver automáticamente a IA tras inactividad humana
          </p>
          <p className="text-xs text-zinc-500 leading-relaxed max-w-sm">
            Cuando un miembro del equipo deja de atender una conversación en
            modo humano, volverá al modo IA tras este tiempo sin actividad.
            El paciente no quedará esperando si nadie la está gestionando.
          </p>
        </div>
        <Select
          value={String(stats.whatsappHumanTimeoutMinutes ?? 60)}
          onValueChange={handleChangeHumanTimeout}
          disabled={savingTimeout}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="60 min" />
          </SelectTrigger>
          <SelectContent>
            {HUMAN_TIMEOUT_OPTIONS.map((m) => (
              <SelectItem key={m} value={String(m)}>
                {m} min
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* SECCIÓN 3 — Historial de ampliaciones */}
      <div className="space-y-3 pt-4 border-t border-zinc-800">
        <div className="flex items-center gap-2">
          <RefreshCw className="w-4 h-4 text-zinc-400" />
          <p className="text-xs text-zinc-500 uppercase tracking-wider">Historial de ampliaciones</p>
        </div>

        {stats.addonHistory.length === 0 ? (
          <p className="text-sm text-zinc-500">No se han realizado ampliaciones este mes</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left py-2 text-xs text-zinc-500 font-normal">Fecha</th>
                <th className="text-left py-2 text-xs text-zinc-500 font-normal">Créditos</th>
                <th className="text-left py-2 text-xs text-zinc-500 font-normal">Importe</th>
                <th className="text-left py-2 text-xs text-zinc-500 font-normal">Estado</th>
              </tr>
            </thead>
            <tbody>
              {stats.addonHistory.map((h, i) => {
                const { label, cls } = statusLabel(h.status);
                return (
                  <tr key={i} className="border-b border-zinc-800/50 last:border-0">
                    <td className="py-2 text-zinc-300">{formatDate(h.date)}</td>
                    <td className="py-2 text-zinc-300">+{h.creditsAdded}</td>
                    <td className="py-2 text-zinc-300">{h.amount}€</td>
                    <td className="py-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${cls}`}>{label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* SECCIÓN 4 — Info del plan */}
      {hasPlan ? (
        <div className="space-y-3 pt-4 border-t border-zinc-800">
          <p className="text-xs text-zinc-500 uppercase tracking-wider">Tu plan</p>
          <p className="text-sm font-medium text-zinc-200">
            Tu plan{' '}
            <span className="text-white capitalize">
              {stats.plan.charAt(0) + stats.plan.slice(1).toLowerCase()}
            </span>{' '}
            incluye:
          </p>
          <ul className="space-y-2">
            {(planItems.length > 0
              ? planItems
              : ['Plan básico sin addons incluidos']
            ).concat(['Reset automático en cada fecha de facturación'])
             .map((item) => (
              <li key={item} className="flex items-center gap-2 text-sm text-zinc-400">
                <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="pt-4 border-t border-zinc-800">
          <p className="text-sm text-zinc-500">
            No tienes un plan activo. Contacta con soporte para activar tu suscripción.
          </p>
        </div>
      )}
    </div>
  );
};
